import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { NotificationManager } from "../../../notifications/NotificationManager";
import i18n from "../../../language/i18n";
import { useApp } from "../../../context/AppContextProvider";
import {
  forgetDeactivatedProfile,
  isLearnerProfileDeactivatedError,
} from "../../../utilization/DeactivatedProfileUtilization";
import { BaseLoadingWithText } from "../../../library";
import { loginWithSebitScreenStyles } from "./LoginWithSebitScreenStyles";
import {
  isSebitEntry,
  readSebitLandingParams,
} from "../../../utilization/SebitLoginUtilization";
import { loginWithSebitToken } from "../../../requests/ManagementRequests";
import { useHandoffLogin } from "../../../hooks/useHandoffLogin";
import {
  isServerUnavailableError,
  resolveBackendMessageKey,
} from "../../../utilization/BackendMessageUtilization";
import {
  buildSsoMobileAppHandoff,
  detectPhonePlatform,
  isMobileAppSsoHandoffEnabled,
  MOBILE_APP_SEBIT_PATH,
  SsoMobileAppHandoff,
} from "../../../utilization/MobileAppHandoffUtilization";
import SsoMobileAppHandoffCard from "../../../components/common/SsoMobileAppHandoffCard";

/**
 * SEBIT VCloud SSO landing screen — the learner's entry from the MBA digital campus.
 *
 * SEBIT is the identity provider: the pupil clicks the CATCHUPPER tile and arrives here with
 * `?token=…&clientid=…`. This screen forwards that pair to our backend, which validates it against SEBIT's
 * own service, resolves the pupil and returns a hand-off URL.
 *
 * **One route, two legs.** The backend's hand-off points back at this same path with the token pair in the
 * fragment, so the screen has to tell "SEBIT is starting a login" from "our backend is finishing one" — hence
 * the branch on `isSebitEntry`. A staff member never reaches this screen: the backend resolves their role and
 * returns an admin-web URL, and SEBIT links them there directly.
 *
 * Unlike the K12 screen there is no client-side configuration to check: nothing about SEBIT is built into the
 * frontend, so "is this environment wired up" is the backend's answer to give, and it distinguishes an
 * unconfigured environment from a bad token.
 *
 * **Phones go to the mobile app** (behind `isMobileAppSsoHandoffEnabled`). The web app is not supported on a phone,
 * so the entry leg hands SEBIT's token to the app instead of exchanging it here; the app posts it to the same
 * endpoint. With the app installed, App Links / Universal Links normally open it before this page even loads. When
 * the page does load: Android forwards the token through a package-pinned `intent://` (Play Store fallback); iOS
 * forwards nothing and offers the store — see `buildSsoMobileAppHandoff`.
 */
const LoginWithSebitScreen = () => {
  const navigate = useNavigate();
  const { hash, search } = useLocation();
  const { isFullScreen, setIsFullScreen, setBoarding } = useApp();
  const { completeHandoffLogin, completeHandoffOrNavigate } =
    useHandoffLogin();
  const [mobileAppHandoff, setMobileAppHandoff] =
    useState<SsoMobileAppHandoff | null>(null);

  useEffect(() => {
    if (isFullScreen === false) {
      setIsFullScreen(true);
    }
  }, []);

  useEffect(() => {
    /**
     * Bail out to the password screen WITH a message.
     *
     * Every failure below routes through here. The K12 screen's failures were once bare `return`s, which left
     * the spinner running forever — an expired token, an unprovisioned pupil and a dead provider all looked
     * identical to "still loading", with no way back but the browser's back button.
     */
    const failToLogin = (messageKey: string) => {
      NotificationManager.error(i18n.t(messageKey));
      navigate("/login");
      // While the phone hand-off is on, App.tsx exempts this route from its phone "device not supported" modal, but
      // it sits outside the router and only re-checks on resize — so without this a phone would reach /login with
      // the modal still off.
      window.dispatchEvent(new Event("resize"));
    };

    const startSebitLogin = async () => {
      const { token, clientId } = readSebitLandingParams(search);
      // Strip the credential from the URL and from history BEFORE the network call. SEBIT's token is valid
      // for about five minutes and nothing in their specification makes it single-use, so it must not sit in
      // history, be re-shared, or ride outbound in a Referer.
      window.history.replaceState({}, document.title, window.location.pathname);

      if (!token || !clientId) {
        failToLogin("sebit_login_failed");
        return;
      }

      const phonePlatform = isMobileAppSsoHandoffEnabled()
        ? detectPhonePlatform(window.navigator.userAgent)
        : null;
      if (phonePlatform) {
        // `clientid` keeps SEBIT's lower-case spelling, so the app reads the same shape whether it gets this
        // hand-off or the landing URL itself as an App Link / Universal Link.
        const handoff = buildSsoMobileAppHandoff(
          phonePlatform,
          MOBILE_APP_SEBIT_PATH,
          { token, clientid: clientId }
        );
        setMobileAppHandoff(handoff);
        if (handoff.appUrl) {
          window.location.href = handoff.appUrl;
        }
        return;
      }

      const { data, err } = await loginWithSebitToken({ token, clientId });
      if (err) {
        // Our own API being down (a deploy refuses connections for minutes) is not the provider's fault.
        // Pasif öğrenci: the school has not opened the platform for this learner — the explanation screen.
        if (isLearnerProfileDeactivatedError(err)) {
          forgetDeactivatedProfile();
          setBoarding("PROFILE_INACTIVE");
          return;
        }
        failToLogin(
          isServerUnavailableError(err)
            ? "sso_server_unavailable_text"
            : resolveBackendMessageKey(err, "sebit_login_failed")
        );
        return;
      }

      const redirectUrl = (data as any)?.redirectUrl;
      if (!redirectUrl) {
        failToLogin("sebit_login_failed");
        return;
      }
      // The backend decides which console this role belongs in, so a mis-linked tile self-heals. A learner's
      // hand-off points back at this very path and differs only in the fragment — assigning `location.href`
      // would not reload, leaving the spinner up until a manual refresh. `completeHandoffOrNavigate` finishes
      // that case in-page and navigates only for the cross-console one.
      completeHandoffOrNavigate(redirectUrl, failToLogin, "sebit_login_failed");
    };

    if (isSebitEntry(search)) {
      startSebitLogin();
      return;
    }
    // The return leg: our own hand-off, carrying the pair in the fragment.
    completeHandoffLogin(hash, search, failToLogin);
  }, []);

  if (mobileAppHandoff) {
    return (
      <div className="lws-root">
        <style>{loginWithSebitScreenStyles}</style>
        <SsoMobileAppHandoffCard handoff={mobileAppHandoff} />
      </div>
    );
  }

  return (
    <div className="lws-root">
      <style>{loginWithSebitScreenStyles}</style>
      <BaseLoadingWithText
        size="large"
        primaryColor="#55777f"
        secondaryColor="55777f"
      />
    </div>
  );
};

export default LoginWithSebitScreen;
